import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Users, Calendar, AlertCircle } from 'lucide-react';
import { admissionsService } from '../../../firebase/admissions';
import { useRealtime } from '../../../contexts/RealtimeContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const AdmissionTestsList = () => {
  const { userProfile } = useRealtime();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadTests();
  }, []);

  const loadTests = async () => {
    try {
      setLoading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      if (!institutionId) {
        setLoading(false);
        return;
      }

      const result = await admissionsService.getAdmissionTests(institutionId);
      if (result.success) {
        setTests(result.tests || []);
      }
    } catch (error) {
      toast.error('Error loading tests');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test? All registrations will also be deleted.')) {
      return;
    }

    try {
      setDeletingId(testId);
      const result = await admissionsService.deleteAdmissionTest(testId);
      
      if (result.success) {
        toast.success('Test deleted successfully');
        loadTests();
      } else {
        toast.error('Failed to delete test');
      }
    } catch (error) {
      toast.error('Error deleting test: ' + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admission Tests</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage all admission tests for your institution
          </p>
        </div>
        <button
          onClick={() => navigate('/coaching-admin/admissions/tests/new')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create New Test
        </button>
      </div>

      {tests.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">No admission tests created yet</p>
          <p className="text-gray-500 dark:text-gray-500 text-sm mb-4">
            Create your first admission test to start accepting registrations
          </p>
          <button
            onClick={() => navigate('/coaching-admin/admissions/tests/new')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create First Test
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tests.map((test) => {
            const isLastDatePassed = test.lastDateToRegister 
              ? new Date(test.lastDateToRegister) < new Date()
              : false;
            const cannotRegister = !test.registrationOpen || isLastDatePassed;

            return (
              <div
                key={test.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                      {test.testName || 'Unnamed Test'}
                    </h3>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {test.enabled ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs font-medium rounded-full">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 text-xs font-medium rounded-full">
                          Inactive
                        </span>
                      )}
                      {test.mandatory && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 text-xs font-medium rounded-full">
                          Mandatory
                        </span>
                      )}
                      {cannotRegister && test.enabled && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 text-xs font-medium rounded-full">
                          Closed
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {test.testDate && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(test.testDate).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {test.lastDateToRegister && (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>
                        Last date: {new Date(test.lastDateToRegister).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => navigate(`/coaching-admin/admissions/tests/${test.id}`)}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm"
                  >
                    <Edit className="w-4 h-4" />
                    Manage
                  </button>
                  <button
                    onClick={() => handleDelete(test.id)}
                    disabled={deletingId === test.id}
                    className="px-3 py-2 bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-200 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdmissionTestsList;
